import { useCallback, useEffect } from 'react';

import { Location } from 'history';
import { GlobalDocumentIds } from '../types/GlobalDocumentIds';
import { murmurhash } from '../lib/js/hash';

const THREE_HOURS = 10800000;
const SCROLL_POSITION_PREFIX = 'scrollPosition-';

export const getScrollPosition = (loc: Location): number => {
  const key = `${SCROLL_POSITION_PREFIX}${murmurhash(loc.pathname)}`;
  const scrollPosition = sessionStorage.getItem(key);
  if (scrollPosition) {
    const { top, timestamp } = JSON.parse(scrollPosition);
    const millisSinceEpoch = Date.now();

    if (millisSinceEpoch - timestamp > THREE_HOURS) {
      sessionStorage.removeItem(key);
      return 0;
    }

    return top;
  }

  return 0;
};

export function useScrollPosition(location: Location, storage: Storage = sessionStorage) {
  /**
   * Saves the scroll position for a given Location.
   * @param loc Location for which to save the current scroll position
   */
  const saveScrollPosition = useCallback(
    (loc: Location) => {
      const key = `${SCROLL_POSITION_PREFIX}${murmurhash(loc.pathname)}`;
      const contentElement = document.getElementById(GlobalDocumentIds.scrollContent);
      const scrollPosition = { top: contentElement?.scrollTop, timestamp: Date.now() };
      storage.setItem(key, JSON.stringify(scrollPosition));
    },
    [storage],
  );

  const saveHandler = () => {
    saveScrollPosition(location);
  };

  // On mount we save last scroll position in storage
  useEffect(() => {
    window.addEventListener('beforeunload', saveHandler);

    return () => window.removeEventListener('beforeunload', saveHandler);
  }, []);

  return { saveScrollPosition };
}
